import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Map, Layers, Globe, ChevronDown, ChevronUp,
  X, Swords, Building2, MapPin, Flag, Compass
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import AppShell from '@/components/AppShell';
import { gradeAccent } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const GRADE_LABELS = {
  7: 'Kansas History', 8: 'US History (8th)', 9: 'World History',
  10: 'Modern World', 11: 'US History (11th)', 12: 'Gov & Econ'
};

const GRADE_COLORS = {
  7: gradeAccent(7).hex, 8: gradeAccent(8).hex, 9: gradeAccent(9).hex,
  10: gradeAccent(10).hex, 11: gradeAccent(11).hex, 12: gradeAccent(12).hex,
};

const MARKER_STYLES = {
  battle:    { color: '#DC2626', icon: Swords,    label: 'Battle' },
  city:      { color: '#2563EB', icon: Building2, label: 'City' },
  event:     { color: '#D97706', icon: Flag,      label: 'Event' },
  territory: { color: '#059669', icon: MapPin,    label: 'Territory' },
  route:     { color: '#7C3AED', icon: Compass,   label: 'Route' },
};

const CONTINENT_PRESETS = [
  { name: 'World', lat: 20, lng: 0, zoom: 2 },
  { name: 'North America', lat: 40, lng: -98, zoom: 4 },
  { name: 'Europe', lat: 50, lng: 15, zoom: 4 },
  { name: 'Africa', lat: 5, lng: 20, zoom: 3 },
  { name: 'Asia', lat: 35, lng: 90, zoom: 3 },
  { name: 'Middle East', lat: 30, lng: 45, zoom: 4 },
  { name: 'Pacific', lat: 10, lng: 150, zoom: 3 },
  { name: 'Kansas', lat: 38.5, lng: -98.5, zoom: 7 },
];

const TILE_LAYERS = {
  political: { url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', attribution: '&copy; OSM &copy; CARTO', label: 'Political' },
  physical:  { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Physical_Map/MapServer/tile/{z}/{y}/{x}', attribution: '&copy; Esri', label: 'Physical' },
  satellite: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '&copy; Esri', label: 'Satellite' },
  terrain:   { url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenTopoMap', label: 'Terrain' },
};

function createMarkerIcon(type) {
  const style = MARKER_STYLES[type] || MARKER_STYLES.event;
  const size = type === 'battle' ? 10 : type === 'city' ? 9 : 8;
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="width:${size}px;height:${size}px;background:${style.color};border-radius:50%;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => { map.flyTo(center, zoom, { duration: 1 }); }, [center, zoom, map]);
  return null;
}

export default function MapExplorer() {
  const navigate = useNavigate();
  const [allMaps, setAllMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [activeMap, setActiveMap] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [tileLayer, setTileLayer] = useState('political');
  const [mapCenter, setMapCenter] = useState([20, 0]);
  const [mapZoom, setMapZoom] = useState(2);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const url = selectedGrade === 'all' ? `${API}/maps` : `${API}/maps?grade=${selectedGrade}`;
        const res = await axios.get(url);
        setAllMaps(res.data || []);
      } catch (e) { toast.error('Failed to load maps'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [selectedGrade]);

  const handleSelectMap = (map) => {
    setActiveMap(map);
    setSelectedMarker(null);
    if (map.center && map.zoom) {
      setMapCenter(map.center);
      setMapZoom(map.zoom);
    }
  };

  const filteredMaps = allMaps;
  const markers = activeMap?.markers || [];

  // Group maps by grade
  const mapsByGrade = {};
  filteredMaps.forEach(m => {
    (m.grade_levels || []).forEach(g => {
      if (!mapsByGrade[g]) mapsByGrade[g] = [];
      if (!mapsByGrade[g].find(x => x.id === m.id)) mapsByGrade[g].push(m);
    });
  });

  if (loading) return <AppShell><div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent" /></div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4 flex items-center gap-3">
          <Map className="h-5 w-5 text-[#7A93AC]" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Resources</p>
            <h1 className="font-serif-display text-2xl text-[#14213D] tracking-tight">Interactive Map Explorer</h1>
          </div>
          <Badge className="bg-[#F9F8F4] text-[#14213D] text-[11px] border border-[#EBE7E0] ml-auto">{allMaps.length} maps</Badge>
        </div>

      {/* Grade Filter */}
      <div className="pt-3 pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-[#5A6577] font-medium uppercase tracking-wide mr-1">Grade:</span>
          <button
            className={`h-7 px-3 rounded text-xs font-medium transition-colors ${selectedGrade === 'all' ? 'bg-[#14213D] text-[#FDFBF7]' : 'bg-white text-[#2D3748] hover:bg-[#F9F8F4] border border-[#EBE7E0]'}`}
            onClick={() => { setSelectedGrade('all'); setActiveMap(null); }}
            data-testid="grade-filter-all"
          >All</button>
          {[7,8,9,10,11,12].map(g => (
            <button key={g}
              className={`h-7 px-3 rounded text-xs font-medium transition-colors border ${selectedGrade === String(g) ? 'text-white' : 'bg-white text-[#2D3748] hover:bg-[#F9F8F4] border-[#EBE7E0]'}`}
              style={selectedGrade === String(g) ? { backgroundColor: GRADE_COLORS[g], borderColor: GRADE_COLORS[g], color: 'white' } : {}}
              onClick={() => { setSelectedGrade(String(g)); setActiveMap(null); }}
              data-testid={`grade-filter-${g}`}
            >{g}: {GRADE_LABELS[g]}</button>
          ))}
        </div>
      </div>

      <div className="pb-4">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          {/* Sidebar - Map List */}
          <div className="lg:col-span-1 space-y-2">
            <Card className="p-3 max-h-[78vh] overflow-y-auto" data-testid="map-sidebar">
              {selectedGrade === 'all' ? (
                [7,8,9,10,11,12].map(g => {
                  const maps = mapsByGrade[g] || [];
                  if (maps.length === 0) return null;
                  return (
                    <GradeSection key={g} grade={g} maps={maps} activeMap={activeMap} onSelect={handleSelectMap} />
                  );
                })
              ) : (
                <div className="space-y-1">
                  {filteredMaps.map(m => (
                    <MapItem key={m.id} map={m} isActive={activeMap?.id === m.id} onSelect={handleSelectMap} />
                  ))}
                </div>
              )}
            </Card>

            {/* Controls */}
            {activeMap && (
              <Card className="p-3" data-testid="map-controls">
                <h4 className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide mb-2">Map Style</h4>
                <div className="grid grid-cols-2 gap-1">
                  {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                    <button key={key} onClick={() => setTileLayer(key)}
                      className={`px-2 py-1.5 rounded text-[11px] font-medium transition-colors ${tileLayer === key ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                      data-testid={`tile-${key}`}
                    >{layer.label}</button>
                  ))}
                </div>
                <h4 className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide mt-3 mb-2">Quick Navigate</h4>
                <div className="grid grid-cols-2 gap-1">
                  {CONTINENT_PRESETS.map(c => (
                    <button key={c.name} onClick={() => { setMapCenter([c.lat, c.lng]); setMapZoom(c.zoom); }}
                      className="text-left px-2 py-1 rounded text-[11px] bg-stone-50 text-stone-600 hover:bg-amber-50 hover:text-amber-800 transition-colors"
                      data-testid={`preset-${c.name.toLowerCase().replace(/\s/g,'-')}`}
                    >{c.name}</button>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Main Map Display */}
          <div className="lg:col-span-4">
            {activeMap ? (
              <Card className="overflow-hidden" data-testid="active-map-display">
                {/* Map Header */}
                <div className="p-3 bg-stone-50 border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        {(activeMap.grade_levels || []).map(g => (
                          <span key={g} className="text-[11px] font-semibold px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: GRADE_COLORS[g] + '30', color: GRADE_COLORS[g] === '#FCD34D' ? '#78350F' : GRADE_COLORS[g] }}>
                            Grade {g}
                          </span>
                        ))}
                        <Badge variant="outline" className="text-[11px]">{markers.length} locations</Badge>
                      </div>
                      <h3 className="text-sm font-bold text-stone-900" data-testid="active-map-title">{activeMap.title}</h3>
                      <p className="text-xs text-stone-500 mt-0.5">{activeMap.description}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setActiveMap(null); setSelectedMarker(null); }}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Leaflet Map */}
                <div style={{ height: '550px' }} data-testid="leaflet-map">
                  <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }} zoomControl={false} maxZoom={14} minZoom={2}>
                    <ZoomControl position="topright" />
                    <MapUpdater center={mapCenter} zoom={mapZoom} />
                    <TileLayer url={TILE_LAYERS[tileLayer].url} attribution={TILE_LAYERS[tileLayer].attribution} />

                    {markers.map((m, i) => (
                      <Marker key={`marker-${m.name}-${m.lat}-${m.lng}-${i}`} position={[m.lat, m.lng]} icon={createMarkerIcon(m.type)}
                        eventHandlers={{ click: () => setSelectedMarker(m) }}>
                        <Popup maxWidth={280}>
                          <div style={{ maxWidth: '260px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                              <span style={{
                                display: 'inline-block', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                                backgroundColor: (MARKER_STYLES[m.type]?.color || '#666') + '20',
                                color: MARKER_STYLES[m.type]?.color || '#666'
                              }}>{MARKER_STYLES[m.type]?.label || m.type}</span>
                            </div>
                            <strong style={{ fontSize: '13px' }}>{m.name}</strong>
                            <p style={{ fontSize: '11px', marginTop: '4px', color: '#555', lineHeight: '1.4' }}>{m.description}</p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>

                {/* Legend */}
                <div className="px-3 py-2 bg-stone-50 border-t flex items-center gap-4 flex-wrap">
                  {Object.entries(MARKER_STYLES).map(([key, style]) => {
                    const count = markers.filter(m => m.type === key).length;
                    if (count === 0) return null;
                    return (
                      <span key={key} className="flex items-center gap-1.5 text-[11px] text-stone-600">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: style.color }} />
                        {style.label} ({count})
                      </span>
                    );
                  })}
                </div>

                {/* Selected Marker Detail */}
                {selectedMarker && (
                  <div className="p-4 bg-white border-t" data-testid="marker-detail">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded"
                          style={{
                            backgroundColor: (MARKER_STYLES[selectedMarker.type]?.color || '#666') + '15',
                            color: MARKER_STYLES[selectedMarker.type]?.color || '#666'
                          }}>
                          {MARKER_STYLES[selectedMarker.type]?.label || selectedMarker.type}
                        </span>
                        <h4 className="text-sm font-bold text-stone-900 mt-1">{selectedMarker.name}</h4>
                        <p className="text-xs text-stone-600 mt-1 leading-relaxed">{selectedMarker.description}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedMarker(null)}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ) : (
              /* Map Cards Grid when nothing selected */
              <div>
                <div className="mb-3 text-center">
                  <h2 className="text-lg font-serif text-stone-800">Select a Map to Explore</h2>
                  <p className="text-xs text-stone-500">{allMaps.length} interactive historical maps with {allMaps.reduce((s,m) => s + (m.markers?.length || 0), 0)} curated locations</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredMaps.map(m => (
                    <Card key={m.id}
                      className="p-4 border border-stone-200 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group"
                      onClick={() => handleSelectMap(m)}
                      data-testid={`map-card-${m.id}`}
                    >
                      <div className="flex items-center gap-1.5 mb-2">
                        {(m.grade_levels || []).map(g => (
                          <span key={g} className="text-[11px] font-semibold px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: GRADE_COLORS[g] + '30', color: GRADE_COLORS[g] === '#FCD34D' ? '#78350F' : GRADE_COLORS[g] }}>
                            Gr {g}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-sm font-semibold text-stone-800 group-hover:text-amber-900 transition-colors">{m.title}</h3>
                      <p className="text-xs text-stone-500 mt-1 line-clamp-2">{m.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-stone-400">
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{m.markers?.length || 0} locations</span>
                        {m.markers?.some(mk => mk.type === 'battle') && <span className="flex items-center gap-1"><Swords className="h-3 w-3 text-red-400" />Battles</span>}
                        {m.markers?.some(mk => mk.type === 'city') && <span className="flex items-center gap-1"><Building2 className="h-3 w-3 text-blue-400" />Cities</span>}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </AppShell>
  );
}

function GradeSection({ grade, maps, activeMap, onSelect }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-2">
      <button className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-stone-50 transition-colors"
        onClick={() => setOpen(!open)}>
        <span className="text-xs font-semibold text-stone-800">Grade {grade}: {GRADE_LABELS[grade]}</span>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-stone-400">{maps.length}</span>
          {open ? <ChevronUp className="h-3 w-3 text-stone-400" /> : <ChevronDown className="h-3 w-3 text-stone-400" />}
        </div>
      </button>
      {open && (
        <div className="space-y-0.5 mt-0.5">
          {maps.map(m => <MapItem key={m.id} map={m} isActive={activeMap?.id === m.id} onSelect={onSelect} />)}
        </div>
      )}
    </div>
  );
}

function MapItem({ map, isActive, onSelect }) {
  return (
    <button
      className={`w-full text-left px-2.5 py-2 rounded text-xs transition-colors ${isActive ? 'bg-stone-800 text-white' : 'bg-stone-50 text-stone-700 hover:bg-stone-100'}`}
      onClick={() => onSelect(map)}
      data-testid={`map-item-${map.id}`}
    >
      <div className="font-medium truncate">{map.title}</div>
      <div className={`text-[11px] mt-0.5 ${isActive ? 'text-stone-300' : 'text-stone-400'}`}>
        {map.markers?.length || 0} locations
      </div>
    </button>
  );
}
